import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { useAuth } from '../../hooks/useAuth';
import useTimeSlots from '../../hooks/useTimeSlots';

/**
 * Time Slot Manager Component
 * Allows employers to define their available time slots for interviews
 * Optimized for real-time updates and proper conflict handling
 */
const TimeSlotManager = ({ onSlotsChange, className = '' }) => {
  const { user, userProfile } = useAuth();
  const { timeSlots, loading, error: hookError, stats, operations } = useTimeSlots();
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState(null);
  const [editingSlot, setEditingSlot] = useState(null);

  // Form state for adding/editing time slots
  const [formData, setFormData] = useState({
    dayOfWeek: '',
    startTime: '',
    endTime: '',
    duration: 60,
    maxConcurrentInterviews: 1,
    isRecurring: true,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    bufferTime: 15 // minutes between interviews
  });

  // Days of week options
  const daysOfWeek = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];

  // Sync error states
  useEffect(() => {
    if (hookError) {
      setError(hookError);
    }
  }, [hookError]);

  // Notify parent when slots change
  useEffect(() => {
    if (onSlotsChange && timeSlots) {
      onSlotsChange(timeSlots);
    }
  }, [timeSlots, onSlotsChange]);

  const resetForm = useCallback(() => {
    setFormData({
      dayOfWeek: '',
      startTime: '',
      endTime: '',
      duration: 60,
      maxConcurrentInterviews: 1,
      isRecurring: true,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      bufferTime: 15
    });
    setEditingSlot(null);
  }, []);

  const validateForm = useCallback(() => {
    if (!formData.dayOfWeek || !formData.startTime || !formData.endTime) {
      return 'Please fill in all required fields';
    }

    if (formData.startTime >= formData.endTime) {
      return 'End time must be after start time';
    }

    if (formData.duration < 15 || formData.duration > 240) {
      return 'Duration must be between 15 and 240 minutes';
    }

    // Check for overlapping time slots on the same day
    const overlapping = timeSlots.find(slot => 
      slot.id !== editingSlot?.id &&
      slot.day_of_week === parseInt(formData.dayOfWeek) &&
      (
        (formData.startTime >= slot.start_time && formData.startTime < slot.end_time) ||
        (formData.endTime > slot.start_time && formData.endTime <= slot.end_time) ||
        (formData.startTime <= slot.start_time && formData.endTime >= slot.end_time)
      )
    );

    if (overlapping) {
      return 'This time slot overlaps with an existing slot';
    }

    return null;
  }, [formData, timeSlots, editingSlot]);

  const handleSubmit = useCallback(async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setError(null);

      const slotData = {
        day_of_week: parseInt(formData.dayOfWeek),
        start_time: formData.startTime,
        end_time: formData.endTime,
        duration_minutes: formData.duration,
        max_concurrent_interviews: formData.maxConcurrentInterviews,
        is_recurring: formData.isRecurring,
        timezone: formData.timezone,
        buffer_time_minutes: formData.bufferTime,
        is_active: true
      };

      let result;
      if (editingSlot) {
        // Update existing slot
        result = await operations.updateTimeSlot(editingSlot.id, slotData);
      } else {
        // Create new slot
        result = await operations.createTimeSlot(slotData);
      }

      if (!result.success) {
        throw new Error(result.error);
      }

      // Reset form and close
      resetForm();
      setShowAddForm(false);
    } catch (err) {
      console.error('Error saving time slot:', err);
      setError(err.message || 'Failed to save time slot. Please try again.');
    }
  }, [formData, validateForm, editingSlot, operations, resetForm]);

  const handleEdit = useCallback((slot) => {
    setFormData({
      dayOfWeek: slot.day_of_week.toString(),
      startTime: slot.start_time,
      endTime: slot.end_time,
      duration: slot.duration_minutes,
      maxConcurrentInterviews: slot.max_concurrent_interviews,
      isRecurring: slot.is_recurring,
      timezone: slot.timezone,
      bufferTime: slot.buffer_time_minutes || 15
    });
    setEditingSlot(slot);
    setShowAddForm(true);
    setError(null);
  }, []);

  const handleDelete = useCallback(async (slotId) => {
    if (!window.confirm('Are you sure you want to delete this time slot? This will affect future interview scheduling.')) {
      return;
    }

    try {
      setError(null);
      
      const result = await operations.deleteTimeSlot(slotId);
      
      if (!result.success) {
        throw new Error(result.error);
      }
    } catch (err) {
      console.error('Error deleting time slot:', err);
      setError(err.message || 'Failed to delete time slot. Please try again.');
    }
  }, [operations]);

  const formatTimeRange = useCallback((startTime, endTime) => {
    const formatTime = (time) => {
      const [hours, minutes] = time.split(':').map(Number);
      const hour12 = hours % 12 || 12;
      const ampm = hours >= 12 ? 'PM' : 'AM';
      return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
    };
    
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  }, []);

  const getDayLabel = useCallback((dayOfWeek) => {
    return daysOfWeek.find(day => day.value === dayOfWeek)?.label || 'Unknown';
  }, [daysOfWeek]);

  // Group slots by day of week for better organization
  const groupedSlots = useMemo(() => {
    const grouped = {};
    timeSlots.forEach(slot => {
      const dayLabel = getDayLabel(slot.day_of_week);
      if (!grouped[dayLabel]) {
        grouped[dayLabel] = [];
      }
      grouped[dayLabel].push(slot);
    });
    return grouped;
  }, [timeSlots, getDayLabel]);

  // Use stats from the hook
  const weeklyStats = useMemo(() => ({
    totalHours: stats.totalHours,
    totalMinutes: stats.totalMinutes,
    slotsCount: stats.totalSlots,
    daysActive: stats.daysActive
  }), [stats]);

  if (!user?.id || userProfile?.role !== 'employer') {
    return (
      <div className="p-4 text-center text-text-secondary">
        Time slot management is only available for employers.
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with stats */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Available Time Slots</h3>
          <p className="text-sm text-text-secondary">
            {weeklyStats.slotsCount} slots across {weeklyStats.daysActive} days • 
            {weeklyStats.totalHours}h {weeklyStats.totalMinutes}m per week
          </p>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            resetForm();
            setShowAddForm(!showAddForm);
          }}
          className="flex items-center gap-2"
        >
          <Icon name="Plus" size={16} />
          Add Time Slot
        </Button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2 text-red-700">
            <Icon name="AlertCircle" size={16} />
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {/* Add/Edit Time Slot Form */}
      {showAddForm && (
        <div className="p-4 bg-gray-50 rounded-lg border border-border space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium">
              {editingSlot ? 'Edit Time Slot' : 'Add New Time Slot'}
            </h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowAddForm(false);
                resetForm();
                setError(null);
              }}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Day of Week *</label>
              <select
                value={formData.dayOfWeek}
                onChange={(e) => setFormData(prev => ({ ...prev, dayOfWeek: e.target.value }))}
                className="w-full p-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                required
              >
                <option value="">Select Day</option>
                {daysOfWeek.map(day => (
                  <option key={day.value} value={day.value}>{day.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Interview Duration (minutes) *</label>
              <select
                value={formData.duration}
                onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) }))}
                className="w-full p-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
                <option value={45}>45 minutes</option>
                <option value={60}>60 minutes</option>
                <option value={90}>90 minutes</option>
                <option value={120}>120 minutes</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Start Time *</label>
              <Input
                type="time"
                value={formData.startTime}
                onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">End Time *</label>
              <Input
                type="time"
                value={formData.endTime}
                onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Buffer Time (minutes)</label>
              <select
                value={formData.bufferTime}
                onChange={(e) => setFormData(prev => ({ ...prev, bufferTime: parseInt(e.target.value) }))}
                className="w-full p-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value={0}>No buffer</option>
                <option value={5}>5 minutes</option>
                <option value={10}>10 minutes</option>
                <option value={15}>15 minutes</option>
                <option value={30}>30 minutes</option>
              </select>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isRecurring"
                checked={formData.isRecurring}
                onChange={(e) => setFormData(prev => ({ ...prev, isRecurring: e.target.checked }))}
                className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
              />
              <label htmlFor="isRecurring" className="text-sm font-medium">
                Recurring weekly
              </label>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={loading}
              className="flex items-center gap-2"
            >
              {loading && <Icon name="Loader" size={16} className="animate-spin" />}
              {editingSlot ? 'Update Slot' : 'Add Slot'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowAddForm(false);
                resetForm();
                setError(null);
              }}
              disabled={loading}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Existing Time Slots */}
      {loading && timeSlots.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Loader" size={24} className="animate-spin mx-auto mb-2" />
          <p className="text-text-secondary">Loading time slots...</p>
        </div>
      ) : Object.keys(groupedSlots).length === 0 ? (
        <div className="text-center py-8 border border-border rounded-lg bg-white">
          <Icon name="Clock" size={48} className="mx-auto mb-4 text-text-secondary" />
          <h4 className="font-medium mb-2">No time slots configured</h4>
          <p className="text-text-secondary text-sm mb-4">
            Add your available time slots to make interview scheduling easier for candidates.
          </p>
          <Button
            variant="primary"
            onClick={() => {
              resetForm();
              setShowAddForm(true);
            }}
          >
            Add Your First Time Slot
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedSlots).map(([day, slots]) => (
            <div key={day} className="border border-border rounded-lg p-4 bg-white">
              <h4 className="font-medium mb-3 text-text-primary">{day}</h4>
              <div className="space-y-2">
                {slots.map(slot => (
                  <div key={slot.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <div className="font-medium">
                        {formatTimeRange(slot.start_time, slot.end_time)}
                      </div>
                      <div className="text-sm text-text-secondary">
                        {slot.duration_minutes} min interviews • 
                        Max {slot.max_concurrent_interviews} concurrent
                        {slot.buffer_time_minutes > 0 && ` • ${slot.buffer_time_minutes} min buffer`}
                        {slot.is_recurring && ' • Recurring'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(slot)}
                        className="text-primary hover:text-primary-dark hover:bg-primary-50"
                        title="Edit time slot"
                      >
                        <Icon name="Edit2" size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(slot.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete time slot"
                      >
                        <Icon name="Trash2" size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TimeSlotManager;